import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, LOCALE_ID, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { AuthService } from 'src/app/Auth/AuthService';
import { environment } from '@env';

@Component({
  selector: 'app-forgotpassword',
  standalone: true,
  imports: [RouterLink, ReactiveFormsModule, CommonModule, TranslateModule],
  templateUrl: './forgot.component.html',
  styleUrls: ['./forgot.component.scss', '../../../../auth.scss'],

})
export class ForgotComponent implements OnInit {
  baseUrl = environment.baseUrl;
  ForgetPasswordForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private route: Router,
    private authService: AuthService,
    private pageService: PagesService,
    private sharedService: SharedService,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.ForgetPasswordForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
    const sessionId = new URLSearchParams(window.location.search).get(
      'session_id'
    );
    if (sessionId) {
      const data = {
        session_id: sessionId,
      };
      this.pageService.update('CHECK_PAYMENT', data).subscribe({
        next: (response: any) => {
          const isSuccess = response?.data?.isSuccess || response?.isSuccess || response?.status === 'paid' || response?.data?.status === 'paid';
          if (isSuccess || !response?.error) {
            this.route.navigate(['/auth/payment-approved'], {
              queryParams: { session_id: sessionId }
            });
          }
        },
      });
    }
  }

  sendResetLink() {
    this.sharedService.showLoader();
    this.pageService
      .create('SEND_RESET_LINK', { email: this.ForgetPasswordForm.value.email })
      .subscribe({
        next: (response: any) => {
          this.sharedService.hideLoader();
          if (response.isSuccess) {
            // The backend's `message` field is always returned in English regardless of
            // site locale, so show our own translated copy instead of the raw API text.
            this.sharedService.createNotification(
              'success',
              this.translate.instant('common.reset-link-sent')
            );

            const url = new URL(window.location.href);
            url.searchParams.delete('session_id');
            window.history.replaceState({}, '', url.toString());
            // The new password is set from the emailed link (/auth/reset-password/<token>);
            // the token-less path is not a route and fell through to the login catch-all anyway.
            this.route.navigate(['/auth/login']);
          } else {
            this.showResetFailure(response, 200);
          }
        },
        error: (err: any) => {
          this.sharedService.hideLoader();
          this.showResetFailure(err?.error, err?.status);
        },
      });
  }

  // An unknown email gets a clear message under the field instead of a reset email.
  private showResetFailure(body: any, status?: number) {
    if (!isEmailNotRegistered(body, status)) {
      this.sharedService.createNotification('error', this.translate.instant('common.reset-link-failed'));
      return;
    }
    const email = this.ForgetPasswordForm.controls['email'];
    email.setErrors({ ...(email.errors ?? {}), notRegistered: true });
    email.markAsTouched();
    this.sharedService.createNotification('error', this.translate.instant('forgot.email-not-registered'));
  }
}

const NOT_REGISTERED_CODES = ['EMAIL_NOT_REGISTERED', 'EMAIL_NOT_FOUND', 'USER_NOT_FOUND', 'NOT_REGISTERED', 'NOT_FOUND'];

/** Whether the reset-link refusal says no account uses the email. */
export function isEmailNotRegistered(body: any, status?: number): boolean {
  if (status === 404) return true;
  const code = String(body?.code ?? body?.error?.code ?? '').trim().toUpperCase();
  if (NOT_REGISTERED_CODES.includes(code)) return true;
  const message = String(body?.message ?? body?.error?.message ?? '');
  return /not (found|registered|exist)|does ?n[o']t exist|no (user|account)/i.test(message);
}
